import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {
  useGetSuppliersQuery,
  useGetSupplierQuery,
  useToggleSupplierStatusMutation,
} from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { SupplierFormContent } from './SupplierForm';

function SuppliersList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const { data: suppliersData, isLoading, error } = useGetSuppliersQuery({ all: 'true' });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const suppliers = Array.isArray(suppliersData) ? suppliersData : suppliersData?.suppliers ?? [];

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const [toggleStatus] = useToggleSupplierStatusMutation();
  const { data: editingSupplier, isLoading: loadingSupplier } = useGetSupplierQuery(editingId, {
    skip: !editingId,
  });

  const filteredSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) => {
      return (
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.code?.toLowerCase().includes(q) ||
        s.phone?.toLowerCase().includes(q) ||
        s.taxId?.toLowerCase().includes(q)
      );
    });
  }, [suppliers, search]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (id) => {
    setEditingId(id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleToggleStatus = (id) => {
    toggleStatus(id);
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading suppliers</Typography>;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/purchases')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Suppliers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ borderRadius: 2 }}>
          Add Supplier
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search suppliers"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, code, phone or tax id..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500 }}
        />
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tax ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Credit Balance (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.length > 0 ? (
                  filteredSuppliers.map((supplier) => (
                    <TableRow key={supplier.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{supplier.name}</TableCell>
                      <TableCell>{supplier.code || '-'}</TableCell>
                      <TableCell>{supplier.email || '-'}</TableCell>
                      <TableCell>{supplier.phone || '-'}</TableCell>
                      <TableCell>{supplier.taxId || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>{supplier.address || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: Number(supplier.creditBalance) > 0 ? 'warning.main' : 'text.secondary' }}>
                        {formatAmount(supplier.creditBalance)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!supplier.isActive}
                          onChange={() => handleToggleStatus(supplier.id)}
                          color="primary"
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4ECDC4',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4ECDC4',
                            },
                          }}
                        />
                        <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                          {supplier.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/purchases/suppliers/${supplier.id}`)}
                          sx={{ mr: 0.5 }}
                          title="View supplier"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/purchases/orders/new?supplierId=${supplier.id}`)}
                          sx={{ mr: 0.5 }}
                          title="Create purchase order"
                        >
                          <ShoppingCartIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/purchases/orders/new?supplierId=${supplier.id}&lpo=1`)}
                          sx={{ mr: 0.5 }}
                          title="Create LPO (Local Purchase Order)"
                        >
                          <AssignmentIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary" onClick={() => handleOpenEdit(supplier.id)} title="Edit supplier">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No suppliers found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {editingId ? 'Edit Supplier' : 'Add Supplier'}
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <SupplierFormContent
            supplier={editingId ? editingSupplier : null}
            isEdit={!!editingId}
            loading={!!editingId && loadingSupplier}
            onSuccess={() => handleCloseDialog()}
            onCancel={handleCloseDialog}
            compact
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default SuppliersList;

